import ReactGA from "react-ga4";
import { Helmet } from "react-helmet";
import Grid from '@mui/material/Grid';
import { Paper } from "@mui/material";
import React, { useContext, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import { useParams, useNavigate } from 'react-router-dom';

import { PATHS } from '../type/EnumPaths';
import { arrayToString } from "../util/formatArrayToString";
import { initRecentProducts } from "../util/initRecentProducts";
import { storeRecentProducts } from "../util/storeRecentProducts";

import { IProduct } from "../type/IProduct";
import { scrollToE } from "../util/scrollToE";
import { ProductContext } from "../util/useContext";
import { getRelatedProductsOfProduct } from "../util/getRelatedProductsOfProduct";

import Price from "../component/Price";
import BrandLink from "../component/BrandLink";
import { Youtube } from "../component/Youtube";
import PayButtons from "../component/PayButtons";

const Hashtags = React.lazy(() => import("../component/Hashtags"));
const ProductImage = React.lazy(() => import("../component/ProductImage"));
const ProductDetail = React.lazy(() => import("../component/ProductDetail"));
const ProductCarousel = React.lazy(() => import("../component/ProductCarousel"));
const RouteBreadcrumbs = React.lazy(() => import("../component/RouteBreadcrumbs"));
const ProductImageModal = React.lazy(() => import("../component/ProductImageModal"));
const ProductRecentlyViewed = React.lazy(() => import("../component/ProductRecentlyViewed"));

const renderSpecialOrder = (specialOrder?: boolean) => {
  if (!specialOrder) return null;

  return (
    <Typography>Special order!</Typography>
  )
}

export const Product = () => {
  const { id } = useParams();
  const navi = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [openModal, setOpenModal] = React.useState(false);
  const { productMap, productHashtagMap } = useContext(ProductContext);
  const [product, setProduct] = React.useState<IProduct | null>(null);
  const [relatedProducts, setRelatedProducts] = React.useState<IProduct[]>([]);
  const [currentUrl, setCurrentUrl] = React.useState(product?.images ? product?.images[0] : "");
  const [recentProducts, setRecentProducts] = React.useState<IProduct[]>(initRecentProducts(productMap));
  
  const toggleModal = (url: string) => {
    setCurrentUrl(url);
    setOpenModal(!openModal);
  }

  useEffect(() => {
    ReactGA.event("view_item");
    if (id && !loading) {
      storeRecentProducts(id);
      setRecentProducts(initRecentProducts(productMap));
      const p = productMap[id];
      if (!p) {
        navi(`/${PATHS.PRODUCTS}`);
      } else {
        setProduct(p);
        setRelatedProducts(getRelatedProductsOfProduct(p, productHashtagMap));

        scrollToE("PRODUCT_TOP");
      }
    }
    if (loading) {
      if (Object.keys(productMap).length > 0) setLoading(false);
    }
  }, [id, navi, productMap, productHashtagMap, loading]);


  if (product === null) return <React.Fragment />;

  return (
    <React.Fragment>
      <Helmet>
        <title>{`${product.name} - Audio500`}</title>
        <meta name="description" content={product.description} />
        <meta name="keywords" content={`${product.name}, Singapore, ${arrayToString(product.metadata?.hashtags)}`} />
        <link rel="cannonical" href={`https://www.audio500.com/products/${product.id}`} />
      </Helmet>
      <Grid
        container
        spacing={2}
        flexDirection={"column"}
        justifyContent={"center"}
        sx={{ 
          overflowWrap: "anywhere", 
          background: { xs: "white", sm: "inherit" }, 
          pl: { xs: "20px", sm: "30px", lg: "40px" },
          pr: { xs: "20px", sm: "30px", lg: "40px" } 
        }}
        id={"PRODUCT_TOP"}
      >
        <Grid item paddingBottom={"10px"}>
          <RouteBreadcrumbs />
        </Grid>

        <Grid item paddingTop={"40px"}>
          <Typography
            component="h2"
            variant="h5"
            fontWeight="600"
          >
            {product.name}
          </Typography>
          <Typography
            component="p"
            variant="subtitle1"
          >
            {product.description}
          </Typography>

          <BrandLink manufacturer={product.metadata.manufacturer} />
          <Hashtags hashtags={product.metadata.hashtags} page={PATHS.PRODUCTS} />

        </Grid>

        <Grid item>
          <Grid container flexDirection={"row"}>
              <Grid item xs={12} sm={8} md={9} padding={1}>
                <ProductImage images={product.images} name={product.name} toggleModal={toggleModal} />
              </Grid>
              <Grid item xs={12} sm={4} md={3} padding={1}>
                <Paper variant="outlined" sx={{ padding: "13px", borderRadius: "10px" }}>
                <Price price={product.price?.price} size={30} />
                  {renderSpecialOrder(product.specialOrder)}
                  <PayButtons productName={product.name} productId={product.id} />
                </Paper>
              </Grid>
          </Grid>
        </Grid>

        <Grid item>
          <hr />
        </Grid>
        <ProductDetail details={product.details}/>

        <Youtube videos={product.videos} />

        <Grid item paddingBottom={"80px"} />

        <Grid item>
          <ProductCarousel label="You may also like:" data={relatedProducts} />
        </Grid>

        <ProductRecentlyViewed recentProducts={recentProducts}/>
      </Grid>

      <Grid item paddingBottom={"80px"} />

      <ProductImageModal url={currentUrl} open={openModal}/>
    </React.Fragment>
  )
}